import React, { useEffect, useState } from 'react';
import { ConfigProvider, DatePicker, Pagination, Tooltip } from 'antd';
import { CaretDownOutlined, CaretRightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import viVN from 'antd/locale/vi_VN';
import { getListMemberFluctuationYearly, IListFluctuationYearly } from '../../../../api/reportCustomer/reportCustomerApi';
import '../styles.scss';
import { IDataDefaulFilter } from '..';
import { useSelector } from 'react-redux';
import { selectorInfoUser } from '../../../../store/slices/infoUserSlice';
import { formatNumber } from '../../../../services/mixin';
import { dataPagination } from '../../../../until';

const { RangePicker } = DatePicker;

const TOOLTIP_TEXT =
    'Số lượng tăng trưởng = Số lượng KH của năm hiện tại – Số lượng KH của năm trước tại cùng một hạng thành viên. ' +
    'Nếu khách hàng được nâng hoặc hạ hạng trong năm, hệ thống sẽ ghi nhận theo hạng cuối cùng tại thời điểm cuối năm. ' +
    '% tăng trưởng được tính theo công thức: (Số lượng tăng trưởng / Số lượng KH của năm trước)';

const formatNum = (val: number | null | undefined): string => {
    if (val === null || val === undefined) return '--';
    return String(formatNumber(val));
};

const GrowthCell: React.FC<{ val: number | null | undefined }> = ({ val }) => {
    if (val === null || val === undefined) return <span>--</span>;
    return <span style={{ color: val < 0 ? '#ff4d4f' : 'inherit' }}>{formatNumber(val)}</span>;
};

const thStyle: React.CSSProperties = {
    padding: '0 16px',
    height: 44,
    background: '#0d6bc8',
    color: '#fff',
    fontWeight: 500,
    fontSize: 13,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    borderRight: '1px solid rgba(255,255,255,0.18)',
};

const tdStyle: React.CSSProperties = {
    padding: '0 16px',
    height: 44,
    borderBottom: '1px solid #dce4ef',
    fontSize: 13,
    whiteSpace: 'nowrap',
    borderRight: '1px solid #f0f0f0',
};

interface props {
    dataFilter: IDataDefaulFilter | undefined
}


const ReportFluctuationYearly: React.FC<props> = ({ dataFilter }) => {

    const { unitTemp } = useSelector(selectorInfoUser)


    const [data, setData] = useState<IListFluctuationYearly[]>([]);
    const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set());
    const [fromYear, setFromYear] = useState<string | undefined>();
    const [toYear, setToYear] = useState<string | undefined>();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [total, setTotal] = useState(0);

    const fetchData = async () => {
        const result = await getListMemberFluctuationYearly(
            unitTemp, dataFilter?.unitIds, dataFilter?.rankIds, fromYear, toYear
        );
        if (result?.status) {
            setData(result.data ?? []);
            setTotal(result.data?.length ?? 0);
        }
    };

    useEffect(() => {
        fetchData();
    }, [fromYear, toYear, dataFilter?.rankIds, dataFilter?.unitIds, unitTemp]);

    const toggleYear = (year: number) => {
        setExpandedYears(prev => {
            const next = new Set(prev);
            if (next.has(year)) next.delete(year);
            else next.add(year);
            return next;
        });
    };

    const pagedData = data.slice((page - 1) * pageSize, page * pageSize);

    return (
        <div className="" style={{ marginTop: 12 }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0px', borderBottom: '1px solid #dce4ef' }}>
                <span style={{ fontWeight: 500, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className='text-base font-medium '>Báo cáo số lượng khách hàng thành viên biến động theo năm</span>
                    <Tooltip title={TOOLTIP_TEXT}
                        color='#ffffff'

                        overlayStyle={{ maxWidth: 460 }}
                    >
                        <InfoCircleOutlined style={{ color: '#1677ff', cursor: 'pointer' }} />
                    </Tooltip>
                </span>
                <ConfigProvider locale={viVN}>
                    <RangePicker
                        picker="year"
                        className="custom-input-date"
                        placeholder={['Từ năm', 'Đến năm']}
                        onChange={(dates) => {
                            setFromYear(dates?.[0]?.format('YYYY') ?? undefined);
                            setToYear(dates?.[1]?.format('YYYY') ?? undefined);
                            setPage(1);
                        }}
                    />
                </ConfigProvider>
            </div>

            {/* Table */}
            <div style={{ overflowX: 'auto', border: 12, }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ ...thStyle, width: 120 }}>Năm</th>
                            <th style={{ ...thStyle, width: 180, textAlign: 'left' }}>Hạng thành viên</th>
                            <th style={{ ...thStyle, width: 160 }}>Số lượng KH</th>
                            <th style={{ ...thStyle, width: 200 }}>Số lượng tăng trưởng</th>
                            <th style={{ ...thStyle, width: 160, borderRight: 'none' }}>% tăng trưởng</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pagedData.map((item, yearIndex) => {
                            const isExpanded = expandedYears.has(item.year);
                            const bg = yearIndex % 2 ? '#f7f9fc' : '#fff';

                            if (!isExpanded) {
                                return (
                                    <tr key={item.year} style={{ background: bg }}>
                                        <td style={{ ...tdStyle, cursor: 'pointer' }} onClick={() => toggleYear(item.year)}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <CaretRightOutlined style={{ color: '#1677ff' }} />
                                                {item.year}
                                            </span>
                                        </td>
                                        <td style={tdStyle}>Tổng</td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>{formatNum(item.total.customerCount)}</td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <GrowthCell val={item.total.growthCount} />
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'center', borderRight: 'none' }}>
                                            <GrowthCell val={item.total.growthRate} />
                                        </td>
                                    </tr>
                                );
                            }

                            const allRows: Array<{ rankName: string; customerCount: number; growthCount: number; growthRate: number; isTotal?: boolean }> = [
                                ...item.ranks,
                                { ...item.total, isTotal: true },
                            ];

                            return allRows.map((rank, rankIndex) => (
                                <tr key={`${item.year}-${rank.rankName}-${rankIndex}`} style={{ background: bg }}>
                                    {rankIndex === 0 && (
                                        <td
                                            rowSpan={allRows.length}
                                            style={{ ...tdStyle, verticalAlign: 'top', paddingTop: 12, cursor: 'pointer' }}
                                            onClick={() => toggleYear(item.year)}
                                        >
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <CaretDownOutlined style={{ color: '#1677ff' }} />
                                                {item.year}
                                            </span>
                                        </td>
                                    )}
                                    <td style={{ ...tdStyle, fontWeight: rank.isTotal ? 600 : 400 }}>
                                        {rank.rankName}
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'center' }}>{formatNum(rank.customerCount)}</td>
                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                        <GrowthCell val={rank.growthCount} />
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'center', borderRight: 'none' }}>
                                        <GrowthCell val={rank.growthRate} />
                                    </td>
                                </tr>
                            ));
                        })}
                    </tbody>
                </table>
            </div>
            <ConfigProvider locale={viVN}>
                <div className="pagination-block">
                    <Pagination
                        showSizeChanger
                        current={page}
                        pageSize={pageSize}
                        total={total}
                        itemRender={(page, type, originalElement) => {
                            if (type === 'page') {
                                return <span>{formatNumber(page)}</span>;
                            }
                            return originalElement;
                        }}
                        showTotal={() => `Tổng cộng ${dataPagination(total, pageSize, page).totalPage} trang`}
                        className='rs-pagination pt-4'
                        onChange={(page, size) => {

                            setPage(page);

                            setPageSize(size);

                            /**
                             * load API
                             */

                        }}
                    />
                </div>
            </ConfigProvider>




        </div>
    );
};

export default ReportFluctuationYearly;
